import React, { useState, useEffect, useMemo } from 'react';
import ProductImagePlaceholder from './ProductImagePlaceholder';
import { formatCurrency, getCartTotals, parsePriceValue } from '../utils/cart';

const API_BASE = process.env.REACT_APP_API_URL || '';
let citiesCache = null;
const storesCache = new Map();

function CartItemThumb({ src, alt }) {
  const [imageError, setImageError] = useState(false);

  if (!src || imageError) {
    return (
      <ProductImagePlaceholder
        label={imageError ? 'Image not found' : 'No image'}
        className="cart-item-noimg image-fallback"
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setImageError(true)}
    />
  );
}

function CartPage({ token, selectedProducts, onQuantityChange, onRemove, onPlaceOrder, submitting, error, onClearError, paymentMethod }) {
  const items = useMemo(() => Object.values(selectedProducts), [selectedProducts]);
  const { totalQuantity, totalAmount } = useMemo(() => getCartTotals(items), [items]);

  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citiesError, setCitiesError] = useState('');
  const [selectedCity, setSelectedCity] = useState('');

  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storesError, setStoresError] = useState('');
  const [selectedStoreCode, setSelectedStoreCode] = useState('');

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Fetch pickup cities on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (citiesCache) {
        setCities(citiesCache);
        return;
      }

      setLoadingCities(true);
      setCitiesError('');
      try {
        const res = await fetch(`${API_BASE}/api/cities`, { headers: authHeaders });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load cities');
        if (!cancelled) {
          citiesCache = data.cities || [];
          setCities(citiesCache);
        }
      } catch (e) {
        if (!cancelled) setCitiesError(e.message);
      } finally {
        if (!cancelled) setLoadingCities(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authHeaders]);

  // Fetch pickup-eligible stores whenever the city changes.
  useEffect(() => {
    setSelectedStoreCode('');
    setStores([]);
    setStoresError('');
    if (!selectedCity) return;

    let cancelled = false;
    (async () => {
      if (storesCache.has(selectedCity)) {
        setStores(storesCache.get(selectedCity));
        return;
      }

      setLoadingStores(true);
      try {
        const res = await fetch(`${API_BASE}/api/stores?city=${encodeURIComponent(selectedCity)}`, { headers: authHeaders });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Could not load stores');
        if (!cancelled) {
          const nextStores = data.stores || [];
          storesCache.set(selectedCity, nextStores);
          setStores(nextStores);
        }
      } catch (e) {
        if (!cancelled) setStoresError(e.message);
      } finally {
        if (!cancelled) setLoadingStores(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCity, authHeaders]);

  const selectedStore = stores.find(s => (s.code || s.erp_id) === selectedStoreCode);
  const canPlaceOrder = items.length > 0 && !!selectedCity && !!selectedStore && !submitting;

  const handlePlaceOrder = () => {
    if (!canPlaceOrder) return;
    const label = `${selectedStore.pickup_location_name || selectedStore.name}${selectedStore.code ? ` (${selectedStore.code})` : ''}`;
    onPlaceOrder(selectedCity, label);
  };

  const stepQty = (product, delta) => {
    const maxStock = (() => {
      const n = parseFloat(product.stock);
      return isNaN(n) || n <= 0 ? 999 : Math.floor(n);
    })();
    const next = Math.min(Math.max(1, (product.quantity || 1) + delta), maxStock);
    if (next !== (product.quantity || 1)) onQuantityChange(product.id, next);
  };

  return (
    <div className="cart-page">
      <main className="cart-page-main">
        {error && (
          <div className="error-message cart-page-error">
            {error}
            <button onClick={onClearError} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>x</button>
          </div>
        )}

        {items.length === 0 ? (
          <div className="empty-state">
            <h3>Your cart is empty</h3>
            <p>Add some products, then come back to place your order.</p>
          </div>
        ) : (
          <div className="cart-page-shell">
            <section className="cart-items-panel">
              <div className="cart-panel-header">
                <div>
                  <h2>Cart</h2>
                  <p>{items.length} item{items.length !== 1 ? 's' : ''} · {totalQuantity} qty</p>
                </div>
                <div className="cart-panel-total">{formatCurrency(totalAmount)}</div>
              </div>

              <div className="cart-items cart-items-list">
                {items.map(item => (
                  <div className="cart-item" key={item.id}>
                    <div className="cart-item-thumb">
                      <CartItemThumb src={item.image} alt={item.description} />
                    </div>

                    <div className="cart-item-info">
                      <div className="cart-item-sku">{item.sku}</div>
                      <div className="cart-item-desc">{item.description}</div>
                      <div className="cart-item-tags">
                        <span className={`cart-item-custom ${item.customization === 'yes' ? 'yes' : ''}`}>
                          {item.customization === 'yes'
                            ? `Customized${item.customizationFile ? `: ${item.customizationFile.name}` : ''}`
                            : 'No customization'}
                        </span>
                      </div>
                    </div>

                    <div className="cart-item-qty">
                      <span className="cart-item-label">Qty</span>
                      <div className="qty-stepper">
                        <button onClick={() => stepQty(item, -1)} disabled={(item.quantity || 1) <= 1} aria-label="Decrease quantity">&minus;</button>
                        <span className="qty-value">{item.quantity || 1}</span>
                        <button onClick={() => stepQty(item, 1)} aria-label="Increase quantity">+</button>
                      </div>
                    </div>

                    <div className="cart-item-price-block">
                      <span className="cart-item-label">Price</span>
                      {item.price && <span className="cart-item-price">{formatCurrency(parsePriceValue(item.price))}</span>}
                      {item.price && (
                        <span className="cart-item-total">
                          Total {formatCurrency(parsePriceValue(item.price) * (item.quantity || 1))}
                        </span>
                      )}
                      <button className="cart-item-remove" onClick={() => onRemove(item.id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <aside className="cart-side-panel">
              <div className="pickup-section">
                <h2>Pickup location</h2>
                <p className="pickup-hint">Select the Printo store where you'll collect this order.</p>

                <label className="pickup-field">
                  <span>City</span>
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    disabled={loadingCities}
                  >
                    <option value="">{loadingCities ? 'Loading cities…' : 'Select a city'}</option>
                    {cities.map(c => (
                      <option key={c.id ?? c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </label>
                {citiesError && <p className="pickup-error">{citiesError}</p>}

                <label className="pickup-field">
                  <span>Store</span>
                  <select
                    value={selectedStoreCode}
                    onChange={(e) => setSelectedStoreCode(e.target.value)}
                    disabled={!selectedCity || loadingStores}
                  >
                    <option value="">
                      {!selectedCity ? 'Select a city first'
                        : loadingStores ? 'Loading stores…'
                        : stores.length === 0 ? 'No pickup stores available'
                        : 'Select a store'}
                    </option>
                    {stores.map(s => (
                      <option key={s.code || s.erp_id} value={s.code || s.erp_id}>
                        {(s.pickup_location_name || s.name)}{s.postal_code ? ` — ${s.postal_code}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                {storesError && <p className="pickup-error">{storesError}</p>}

                {selectedStore && (
                  <div className="store-detail">
                    <strong>{selectedStore.pickup_location_name || selectedStore.name}</strong>
                    {selectedStore.state && <span>{selectedStore.city_name}, {selectedStore.state} {selectedStore.postal_code || ''}</span>}
                    {selectedStore.primary_contact_number && <span>{selectedStore.primary_contact_number}</span>}
                  </div>
                )}
              </div>

              <div className="cart-page-footer">
                <button
                  className="confirm-btn place-order-btn"
                  onClick={handlePlaceOrder}
                  disabled={!canPlaceOrder}
                >
                  {submitting
                    ? (paymentMethod === 'RAZORPAY' ? 'Opening payment…' : 'Placing order…')
                    : (paymentMethod === 'RAZORPAY' ? 'Pay with Razorpay' : 'Place Order')}
                </button>
                <span className="place-order-hint">Select a pickup city and store to place your order.</span>
              </div>
            </aside>
          </div>
        )}
      </main>
    </div>
  );
}

export default CartPage;
